import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Platform, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Audio } from 'expo-av';
import { colors, spacing, chat } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

const { width: screenWidth } = Dimensions.get('window');
const isNativeMobile = Platform.OS !== 'web';

export interface VoiceNotePlayerProps {
  /** Already-resolved, playable media URL (proxy-resolved by the caller). */
  mediaUrl: string;
  /** Sent-by-me styling (white/translucent controls) vs received (primary-color controls). */
  isOutgoing?: boolean;
  /** Sent as an Authorization header on native only — mirrors MessageBubble's voice-note loader. */
  authToken?: string | null;
}

const formatAudioTime = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Voice-note style audio player — play/pause, seekable waveform, speed switcher, elapsed/
 * total timer. Originally built inline inside MessageBubble.tsx for WhatsApp voice notes;
 * extracted so InnoCalls call recordings (CallHistoryScreen.tsx) can use the exact same
 * player instead of a second, simplified implementation.
 */
export const VoiceNotePlayer: React.FC<VoiceNotePlayerProps> = ({ mediaUrl, isOutgoing = false, authToken }) => {
  const styles = useThemedStyles(getStyles);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [waveformWidth, setWaveformWidth] = useState(0);
  const soundRef = useRef<Audio.Sound | null>(null);
  const waveformRef = useRef<View>(null);

  const forceResetAudio = async () => {
    try {
      setCurrentPosition(0);
      setIsPlaying(false);
      if (soundRef.current) {
        await soundRef.current.setPositionAsync(0);
      }
    } catch (error) {
      console.error('🎵 Failed to force reset audio:', error);
    }
  };

  const loadAudio = async () => {
    try {
      if (!mediaUrl) return;
      setIsLoadingAudio(true);

      if (soundRef.current) {
        const currentStatus = await soundRef.current.getStatusAsync();
        if (currentStatus.isLoaded) {
          setIsLoadingAudio(false);
          return; // Audio is already loaded, don't reload
        }
      }

      const source: { uri: string; headers?: Record<string, string> } = {
        uri: mediaUrl,
        ...(authToken && Platform.OS !== 'web' && !mediaUrl.includes('/api/storage/media/proxy') ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
      };
      const loadTimeoutMs = 20000;
      const createPromise = Audio.Sound.createAsync(source, { shouldPlay: false });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Audio load timed out after ${loadTimeoutMs / 1000}s`)), loadTimeoutMs)
      );
      const { sound } = await Promise.race([createPromise, timeoutPromise]);

      soundRef.current = sound;

      const status = await sound.getStatusAsync();
      if (status.isLoaded && status.durationMillis != null && status.durationMillis > 0) {
        setAudioDuration(status.durationMillis);
      } else {
        // Retry after a short delay - some sources report duration only after load
        setTimeout(async () => {
          try {
            const s = await sound.getStatusAsync();
            if (s.isLoaded && s.durationMillis != null && s.durationMillis > 0) {
              setAudioDuration(s.durationMillis);
            }
          } catch (_) { /* ignore */ }
        }, 400);
      }

      sound.setOnPlaybackStatusUpdate(async (status) => {
        if (status.isLoaded) {
          setIsPlaying(status.isPlaying);
          setCurrentPosition(status.positionMillis || 0);
          if (status.durationMillis != null && status.durationMillis > 0) {
            setAudioDuration((prev) => prev > 0 ? prev : status.durationMillis!);
          }
          if (status.didJustFinish && status.positionMillis > 1000) {
            await forceResetAudio();
          }
        }
      });

      if (playbackRate !== 1.0) {
        await sound.setRateAsync(playbackRate, true);
      }
    } catch (error: any) {
      console.error('🎵 Failed to load audio:', error?.message ?? String(error), error?.code ?? '', error);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const validateAudioFile = async () => {
    try {
      if (!mediaUrl) return false;
      // blob:/data: URLs are already local content (e.g. CallHistoryScreen's
      // recording player, which fetches with auth then hands VoiceNotePlayer
      // a blob: URL) — always reachable, and browsers don't support HEAD on
      // them at all (confirmed: net::ERR_METHOD_NOT_SUPPORTED), so skip the
      // reachability check entirely rather than let it always fail closed.
      if (mediaUrl.startsWith('blob:') || mediaUrl.startsWith('data:')) return true;
      const response = await fetch(mediaUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('🎵 Failed to validate audio file:', error);
      return false;
    }
  };

  const playAudio = async () => {
    try {
      if (!mediaUrl) return;

      if (!soundRef.current) {
        setIsLoadingAudio(true);
        if (Platform.OS === 'web') {
          await new Promise<void>(resolve => setTimeout(resolve, 0));
        }
      }

      if (Platform.OS === 'web') {
        const isValidAudio = await validateAudioFile();
        if (!isValidAudio) {
          setIsLoadingAudio(false);
          return;
        }
      }

      if (!soundRef.current) {
        await loadAudio();
      }

      if (soundRef.current) {
        const status = await soundRef.current.getStatusAsync();
        if (status.isLoaded && status.isPlaying) {
          await soundRef.current.pauseAsync();
        } else {
          if (status.isLoaded && status.didJustFinish) {
            await soundRef.current.setPositionAsync(0);
            setCurrentPosition(0);
          }
          await soundRef.current.playAsync();
        }
      }
    } catch (error) {
      console.error('🎵 Failed to play/pause audio:', error);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const changePlaybackRate = async (rate: number) => {
    try {
      if (soundRef.current) {
        await soundRef.current.setRateAsync(rate, true);
        setPlaybackRate(rate);
      }
    } catch (error) {
      console.error('🎵 Failed to change playback rate:', error);
    }
  };

  const seekToPosition = async (position: number) => {
    try {
      if (soundRef.current && audioDuration > 0) {
        const clampedPosition = Math.max(0, Math.min(position, audioDuration));
        await soundRef.current.setPositionAsync(clampedPosition);
        setCurrentPosition(clampedPosition);
      }
    } catch (error) {
      console.error('🎵 Failed to seek to position:', error);
    }
  };

  // Auto-load audio metadata when the source changes
  React.useEffect(() => {
    if (!mediaUrl) return;

    let cleanup: (() => void) | undefined;
    // Web: get duration from HTML5 Audio so it shows before play (expo-av often reports
    // duration only after play on web).
    if (Platform.OS === 'web' && typeof globalThis !== 'undefined' && (globalThis as any).Audio) {
      const AudioConstructor = (globalThis as any).Audio;
      const el = new AudioConstructor();
      el.src = mediaUrl;
      const onLoadedMetadata = () => {
        if (el.duration != null && isFinite(el.duration) && el.duration > 0) {
          setAudioDuration(Math.round(el.duration * 1000));
        }
      };
      el.addEventListener('loadedmetadata', onLoadedMetadata);
      el.load();
      cleanup = () => {
        el.removeEventListener('loadedmetadata', onLoadedMetadata);
        el.src = '';
      };
    }

    loadAudio();
    return () => {
      cleanup?.();
    };
  }, [mediaUrl]);

  // Cleanup audio on unmount — pause first, then unload; a bare unloadAsync() on an
  // actively-playing sound has been observed to occasionally leave audio audibly
  // playing on web if the unload rejects/races with an in-flight play, so stop
  // playback explicitly before releasing the sound rather than relying on unload
  // alone to silence it.
  React.useEffect(() => {
    return () => {
      const sound = soundRef.current;
      if (sound) {
        (async () => {
          try {
            await sound.pauseAsync();
          } catch (_) { /* already stopped/unloaded */ }
          try {
            await sound.unloadAsync();
          } catch (error) {
            console.warn('🎵 Failed to unload audio on unmount:', error);
          }
        })();
      }
    };
  }, []);

  return (
    <View style={styles.audioContainer}>
      <View style={styles.audioPlayer}>
        <TouchableOpacity
          style={[styles.playButton, isOutgoing && styles.playButtonOutgoing]}
          onPress={playAudio}
          activeOpacity={0.7}
          disabled={isLoadingAudio}
        >
          {isLoadingAudio ? (
            <View style={{ width: 32, height: 32, justifyContent: 'center', alignItems: 'center' }}>
              <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.primary} />
            </View>
          ) : (
            <Ionicons
              name={isPlaying ? 'pause-circle' : 'play-circle'}
              size={32}
              color={isOutgoing ? chat.out.accent : colors.primary}
            />
          )}
        </TouchableOpacity>

        <View style={styles.audioInfo}>
          <TouchableOpacity
            ref={waveformRef}
            style={styles.waveform}
            onLayout={(e) => setWaveformWidth(e.nativeEvent.layout.width)}
            onPress={(event) => {
              if (audioDuration > 0 && waveformWidth > 0) {
                const locationX = event.nativeEvent.locationX;
                const seekPercentage = Math.max(0, Math.min(1, locationX / waveformWidth));
                seekToPosition(seekPercentage * audioDuration);
              }
            }}
            activeOpacity={0.8}
          >
            {[...Array(30)].map((_, i) => {
              const isActive = isPlaying && (i < (currentPosition / audioDuration) * 30);
              const progress = currentPosition / audioDuration;
              const barIndex = i / 30;

              return (
                <View
                  key={i}
                  style={[
                    styles.waveformBar,
                    {
                      height: isActive ? 20 : Math.random() * 15 + 5,
                      backgroundColor: isOutgoing
                        ? (isActive ? chat.out.accent : chat.out.line)
                        : (isActive ? colors.primary : withAlpha(colors.primary, 0.25)),
                      opacity: progress > barIndex ? 0.8 : 0.4
                    }
                  ]}
                />
              );
            })}
          </TouchableOpacity>
        </View>

        <View style={styles.audioStatus}>
          {isLoadingAudio ? (
            <ActivityIndicator size="small" color={isOutgoing ? chat.out.subtle : colors.textLight} />
          ) : isPlaying ? (
            <View style={styles.playingIndicator}>
              <View style={[styles.pulseDot, { backgroundColor: isOutgoing ? chat.out.accent : colors.primary }]} />
            </View>
          ) : currentPosition >= audioDuration && audioDuration > 0 ? (
            <Ionicons name="refresh" size={16} color={isOutgoing ? chat.out.subtle : colors.textLight} />
          ) : (
            <Ionicons name="mic" size={16} color={isOutgoing ? chat.out.subtle : colors.textLight} />
          )}
        </View>

        <TouchableOpacity
          style={styles.speedSwitcher}
          onPress={() => {
            const speeds = [0.5, 1.0, 1.5, 2.0];
            const currentIndex = speeds.indexOf(playbackRate);
            const nextIndex = (currentIndex + 1) % speeds.length;
            changePlaybackRate(speeds[nextIndex]);
          }}
        >
          <Text style={[styles.speedSwitcherText, { color: isOutgoing ? chat.out.subtle : colors.textLight }]}>
            {playbackRate === 1.0 ? '1x' : `${playbackRate}x`}
          </Text>
        </TouchableOpacity>
      </View>

      <Text style={[styles.audioTimer, { color: isOutgoing ? chat.out.subtle : colors.textLight }]}>
        {audioDuration > 0 ? `${formatAudioTime(currentPosition)} / ${formatAudioTime(audioDuration)}` : '--:-- / --:--'}
      </Text>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  audioContainer: {
    marginVertical: spacing.xs,
    width: '100%',
    alignSelf: 'stretch',
    minWidth: 0,
  },
  audioPlayer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: 0,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    ...(isNativeMobile ? {} : { minWidth: 280 }),
  },
  playButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
    flexShrink: 0,
  },
  playButtonOutgoing: {
    backgroundColor: chat.out.chip,
  },
  audioInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  waveform: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    height: 30,
    minWidth: 0,
    justifyContent: 'space-between',
  },
  waveformBar: {
    width: 3,
    marginHorizontal: 0.5,
    borderRadius: 1,
    backgroundColor: colors.primary,
  },
  audioStatus: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: spacing.sm,
    flexShrink: 0,
  },
  playingIndicator: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    opacity: 0.8,
  },
  speedSwitcher: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderWidth: 0.5,
    borderColor: 'rgba(0,0,0,0.15)',
    width: 25,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: spacing.xs,
    flexShrink: 0,
  },
  speedSwitcherText: {
    fontSize: 9,
    fontWeight: '600',
  },
  audioTimer: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: spacing.xs / 2,
  },
});
